import { html, LitElement, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { load, ReCaptchaInstance } from 'recaptcha-v3';
import { validateEmail } from '../../utils/validateEmail';
import { AuthActionPayload } from '../redux/auth-slice/types';
import { store, userAPI } from '../redux/store';
import { UserAccountEvents } from '../types';

@customElement('login-form-controller')
export class LoginFormController extends LitElement {
    @property() formSelector: string = '';
    @property() defaultErrorMessage: string = 'Ocorreu um erro';
    @property({ type: Boolean }) isLoginForm: boolean = false;
    @property({ type: Boolean }) isRegisterForm: boolean = false;
    @property({ type: Boolean }) isForgotPasswordForm: boolean = false;
    @property({ type: Boolean }) isResetPasswordForm: boolean = false;

    recaptcha: ReCaptchaInstance | null = null;

    @state()
    isLoading: boolean = false;

    @state()
    nameErrorMessage: string = '';

    @state()
    emailErrorMessage: string = '';

    @state()
    passwordErrorMessage: string = '';

    @state()
    passwordConfirmErrorMessage: string = '';
    @state()
    submitErrorMessage: string = '';

    @state()
    submitSuccessMessage: string = '';

    get formElement(): HTMLFormElement | null {
        if (!this.formSelector) return null;
        return document.querySelector(this.formSelector);
    }

    get submitButtonElement(): HTMLButtonElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-submit-button="true"]') as HTMLButtonElement;
    }

    get nameInputElement(): HTMLInputElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-name-input="true"]') as HTMLInputElement;
    }

    get emailInputElement(): HTMLInputElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-email-input="true"]') as HTMLInputElement;
    }

    get passwordInputElement(): HTMLInputElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-password-input="true"]') as HTMLInputElement;
    }

    get passwordConfirmInputElement(): HTMLInputElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector(
            '[data-password-confirm-input="true"]'
        ) as HTMLInputElement;
    }

    get tokenInputElement(): HTMLInputElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-token-input="true"]') as HTMLInputElement;
    }

    get nameErrorMessageElement(): HTMLDivElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-name-error-message="true"]') as HTMLDivElement;
    }

    get emailErrorMessageElement(): HTMLDivElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector(
            '[data-email-error-message="true"]'
        ) as HTMLDivElement;
    }

    get passwordErrorMessageElement(): HTMLDivElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector(
            '[data-password-error-message="true"]'
        ) as HTMLDivElement;
    }

    get passwordConfirmErrorMessageElement(): HTMLDivElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector(
            '[data-password-confirm-error-message="true"]'
        ) as HTMLDivElement;
    }

    get submitMessageElement(): HTMLDivElement | null {
        if (!this.formElement) return null;
        return this.formElement.querySelector('[data-submit-message="true"]') as HTMLDivElement;
    }

    protected updated(_changedProperties: PropertyValues): void {
        this.updateErrorState(
            this.emailErrorMessageElement,
            this.emailInputElement,
            this.emailErrorMessage
        );

        this.updateErrorState(
            this.passwordErrorMessageElement,
            this.passwordInputElement,
            this.passwordErrorMessage
        );

        this.updateErrorState(
            this.passwordConfirmErrorMessageElement,
            this.passwordConfirmInputElement,
            this.passwordConfirmErrorMessage
        );

        this.updateSubmitMessage();
        this.updateLoadingState();
    }

    private updateLoadingState(): void {
        if (this.isLoading) {
            this.submitMessageElement?.classList.add('loading');
        } else {
            this.submitMessageElement?.classList.remove('loading');
        }
    }

    private updateErrorState(
        messageElement: HTMLDivElement | null,
        inputElement: HTMLInputElement | null,
        errorMessage: string
    ): void {
        if (!messageElement) return;

        if (errorMessage) {
            messageElement.innerHTML = errorMessage;
            messageElement.classList.add('active');
            inputElement?.classList.add('error');
        } else {
            messageElement.classList.remove('active');
            inputElement?.classList.remove('error');
        }
    }

    private updateSubmitMessage(): void {
        if (!this.submitMessageElement) return;

        this.submitMessageElement.classList.remove('active', 'success', 'error');

        if (this.submitSuccessMessage) {
            this.submitMessageElement.innerHTML = this.submitSuccessMessage;
            this.submitMessageElement.classList.add('active', 'success');
        } else if (this.submitErrorMessage) {
            this.submitMessageElement.innerHTML = this.submitErrorMessage;
            this.submitMessageElement.classList.add('active', 'error');
        }
    }

    clearErrorMessages() {
        this.nameErrorMessage = '';
        this.emailErrorMessage = '';
        this.passwordErrorMessage = '';
        this.passwordConfirmErrorMessage = '';
        this.submitErrorMessage = '';
        this.submitSuccessMessage = '';
    }

    setupInputListeners() {
        this.emailInputElement?.addEventListener('input', () => {
            this.clearErrorMessages();
        });
        this.passwordInputElement?.addEventListener('input', () => {
            this.clearErrorMessages();
        });
        this.passwordConfirmInputElement?.addEventListener('input', () => {
            this.clearErrorMessages();
        });
        const seePasswordElements = this.formElement?.querySelectorAll(
            '[data-see-password="true"]'
        );
        if (seePasswordElements) {
            seePasswordElements.forEach((seePasswordElement) => {
                seePasswordElement.addEventListener('click', () => {
                    const closestInput = seePasswordElement.parentElement?.querySelector('input');
                    if (closestInput) {
                        const isPassword = closestInput.type === 'password';
                        closestInput.type = isPassword ? 'text' : 'password';
                        seePasswordElement.classList.toggle('active', isPassword);
                    }
                });
            });
        }
    }

    async loadRecaptcha() {
        try {
            this.recaptcha = await load('6LejZdYlAAAAAICY3gnKcvD-G0j9OEINESsRJ08K', {
                autoHideBadge: true,
            });
        } catch (error) {
            console.error(error);
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.setupInputListeners();
        this.updateLoadingState();
        this.submitButtonElement?.addEventListener('click', (event) => {
            this.handleSubmit(event);
        });
        if (this.isRegisterForm) {
            this.loadRecaptcha();
        }
    }

    async handleSubmit(event: Event) {
        try {
            if (
                this.isLoading ||
                (!this.isLoginForm &&
                    !this.isRegisterForm &&
                    !this.isForgotPasswordForm &&
                    !this.isResetPasswordForm)
            )
                return;
            event.preventDefault();
            this.clearErrorMessages();
            const email = this.emailInputElement?.value;
            const password = this.passwordInputElement?.value;
            const name = this.nameInputElement?.value;
            if (!this.validateFields()) return;

            this.isLoading = true;

            let recaptchaToken = '';

            if (this.isRegisterForm) {
                if (!this.recaptcha) {
                    this.isLoading = false;
                    this.submitErrorMessage = 'Erro ao carregar reCAPTCHA';
                    return;
                }
                recaptchaToken = await this.recaptcha.execute('signup');
            }

            const formAction = this.isLoginForm
                ? () => store.dispatch(userAPI.endpoints.emailLogin.initiate({ email, password }))
                : this.isRegisterForm
                ? () =>
                      store.dispatch(
                          userAPI.endpoints.emailRegister.initiate({
                              email,
                              password,
                              name,
                              'g-recaptcha-response': recaptchaToken,
                          })
                      )
                : this.isForgotPasswordForm
                ? () => store.dispatch(userAPI.endpoints.recoverPassword.initiate({ email }))
                : this.isResetPasswordForm
                ? () =>
                      store.dispatch(
                          userAPI.endpoints.resetPassword.initiate({
                              password,
                              password_confirmation: this.passwordConfirmInputElement?.value,
                              token: this.tokenInputElement?.value,
                          })
                      )
                : () => {
                      throw new Error('Invalid form type');
                  };

            const { isSuccess, data, isError, error } = await formAction();
            this.isLoading = false;

            if (isSuccess && data) {
                if (this.isResetPasswordForm) {
                    window.location.href = '/login?passwordReset=true';
                } else if (this.isForgotPasswordForm) {
                    this.submitSuccessMessage =
                        '<div class="success-title"><span class="icon-check-circle-filled"></span>Instruções de redefinição enviadas!</div>Se encontrarmos uma conta correspondente, enviaremos as instruções para redefinir sua senha.<br/>Verifique sua caixa de entrada e a pasta de spam.';
                } else {
                    window.dispatchEvent(
                        new CustomEvent(UserAccountEvents.LOGGED, {
                            detail: { uuid: (data as AuthActionPayload).user.uuid },
                        })
                    );
                }
            } else {
                this.clearErrorMessages();
                if (error && 'errorMessage' in error) {
                    this.submitErrorMessage = (error as { errorMessage: string }).errorMessage;
                } else {
                    this.submitErrorMessage = this.defaultErrorMessage;
                }
            }
        } catch (error) {
            this.isLoading = false;
            this.submitErrorMessage = this.defaultErrorMessage;
        }
    }

    validateFields(): boolean {
        let hasErrors = false;

        // Validate email if not on reset password form
        const emailValue = this.emailInputElement?.value;
        if (!this.isResetPasswordForm && (!emailValue || !validateEmail(emailValue))) {
            this.emailErrorMessage = 'Introduza um email válido';
            hasErrors = true;
        }

        // Validate password if not on forgot password form
        const passwordValue = this.passwordInputElement?.value;
        if (!this.isForgotPasswordForm && (!passwordValue || passwordValue.length < 8)) {
            this.passwordErrorMessage = 'A senha deve ter pelo menos 8 caracteres';
            hasErrors = true;
        }

        // Validate password confirmation if on reset password form
        const confirmValue = this.passwordConfirmInputElement?.value;
        if (this.isResetPasswordForm && confirmValue !== passwordValue) {
            this.passwordConfirmErrorMessage = 'As senhas não coincidem';
            hasErrors = true;
        }

        return !hasErrors;
    }

    protected render() {
        return html`<slot></slot>`;
    }
}
